import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { queryKeys } from '@/api/queryKeys';
import { config } from '@/config/app';
import type { QueueTaskInfo } from '@/types/api';
import { TERMINAL_TASK_STATES } from '@/utils/taskProgress';

type ProgressListener = (progress: QueueTaskInfo) => void;

interface TaskProgressContextValue {
  subscribe: (taskId: string, listener: ProgressListener) => void;
  unsubscribe: (taskId: string, listener: ProgressListener) => void;
}

const TaskProgressContext = createContext<TaskProgressContextValue | null>(null);

const WS_URL = `${config.apiUrl.replace(/^http/, 'ws')}/tasks/progress`;
const PING_INTERVAL = 30_000;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30_000;

interface WsMessage {
  type: string;
  data?: QueueTaskInfo;
  message?: string;
}

export const TaskProgressProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const listenersRef = useRef<Map<string, Set<ProgressListener>>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);
  const currentTokenRef = useRef<string | null>(null);
  const taskCacheRef = useRef<Map<string, QueueTaskInfo>>(new Map());
  const invalidatedTasksRef = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const closeWebSocket = useCallback(() => {
    clearPingInterval();
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, [clearPingInterval, clearReconnectTimeout]);

  const handleMessage = useCallback(
    (message: WsMessage) => {
      if (message.type === 'progress' && message.data) {
        const data = message.data;
        listenersRef.current.get(data.id)?.forEach((cb) => cb(data));

        const isTerminal = TERMINAL_TASK_STATES.includes(data.state);
        if (isTerminal) {
          taskCacheRef.current.delete(data.id);
        } else {
          taskCacheRef.current.set(data.id, data);
        }
        const isDetectCollisions = data.task_type === 'detect_collisions';
        if (isTerminal && isDetectCollisions && !invalidatedTasksRef.current.has(data.id)) {
          invalidatedTasksRef.current.add(data.id);
          queryClient.invalidateQueries({ queryKey: queryKeys.projectDetail(data.id) });
          queryClient.invalidateQueries({ queryKey: ['projects'] });
        }
      }
    },
    [queryClient]
  );

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  const connect = useCallback(() => {
    if (!token || isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    isConnectingRef.current = true;
    currentTokenRef.current = token;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'login', token }));
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as WsMessage;

        if (message.type === 'logged_in') {
          isConnectingRef.current = false;
          reconnectAttemptsRef.current = 0;
          pingIntervalRef.current = setInterval(sendPing, PING_INTERVAL);
          return;
        }

        if (message.type === 'pong') {
          return;
        }

        if (message.type === 'error') {
          // biome-ignore lint/suspicious/noConsole: WebSocket server errors should be visible
          console.error('WebSocket error:', message.message);
          return;
        }

        handleMessage(message);
      } catch (err) {
        // biome-ignore lint/suspicious/noConsole: Unexpected WebSocket message parsing errors should be visible
        console.error('Failed to parse WebSocket message:', err);
      }
    };

    ws.onclose = (event) => {
      isConnectingRef.current = false;
      clearPingInterval();

      if (event.code === 4001) {
        return;
      }

      const delay = Math.min(RECONNECT_BASE_DELAY * 2 ** reconnectAttemptsRef.current, RECONNECT_MAX_DELAY);
      reconnectAttemptsRef.current += 1;
      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    ws.onerror = (err) => {
      // biome-ignore lint/suspicious/noConsole: WebSocket connection errors should be visible
      console.error('WebSocket error:', err);
    };
  }, [token, sendPing, clearPingInterval, handleMessage]);

  useEffect(() => {
    if (token) {
      connect();
    } else {
      closeWebSocket();
    }

    return () => {
      closeWebSocket();
    };
  }, [token, connect, closeWebSocket]);

  const subscribe = useCallback((taskId: string, listener: ProgressListener) => {
    let set = listenersRef.current.get(taskId);
    if (!set) {
      set = new Set();
      listenersRef.current.set(taskId, set);
    }
    set.add(listener);

    const cached = taskCacheRef.current.get(taskId);
    if (cached) {
      listener(cached);
    }
  }, []);

  const unsubscribe = useCallback((taskId: string, listener: ProgressListener) => {
    listenersRef.current.get(taskId)?.delete(listener);
  }, []);

  const value = useRef<TaskProgressContextValue>({ subscribe, unsubscribe }).current;

  return <TaskProgressContext.Provider value={value}>{children}</TaskProgressContext.Provider>;
};

export const useTaskProgressContext = (): TaskProgressContextValue => {
  const ctx = useContext(TaskProgressContext);
  if (!ctx) {
    throw new Error('useTaskProgressContext must be used within TaskProgressProvider');
  }
  return ctx;
};
